/**
 * The Mood Jar's gesture physics, as pure functions with no imports.
 *
 * **Nothing here touches react-native, and that is deliberate.** The jar screen
 * pulls in expo-router, react-native and the API client, so nothing in it can
 * be imported by a test — the only assertions available there are scans of its
 * own source text. `background-permission-route.ts` records what that costs:
 * a guard shipped broken behind seven passing tests, every one of them a source
 * scan that proved the guard *ran* and none of them able to prove it could ever
 * *fire*.
 *
 * So the decisions live here and the screen only wires them up. Every function
 * below is a `worklet`, because Reanimated runs them on the UI thread — they
 * must not close over anything from the JS side.
 *
 * ## The four gestures
 *
 * - **Tap** — a stir. Already existed; unchanged in feel.
 * - **Tilt** — bubbles drift to the low side, like liquid finding its level.
 * - **Hold** — bubbles gather to the middle; releasing scatters them out.
 * - **Shake** — a storm, decaying back to rest.
 *
 * They compose additively rather than switching between modes: a jar can be
 * tilted *and* settling from a shake, which is what a real one would do.
 */

/** Gravity's magnitude. Sensor values arrive in m/s^2 and are normalised by it. */
const G = 9.81;

/**
 * Smoothing applied to raw gravity, per sensor frame. Higher answers a lean
 * faster; lower is calmer but laggier. Raised from 0.14, which filtered the
 * jitter well and made the jar feel like it was thinking about it first.
 */
export const TILT_SMOOTHING = 0.24;

/**
 * A shake has to beat this much sustained jerk before the jar storms.
 *
 * Tuned to sit above walking and below a deliberate flick. Too low and the jar
 * storms in somebody's pocket on a staircase; too high and it ignores a genuine
 * shake. It is a threshold on ACCUMULATED jerk rather than a single sample, so
 * one sharp bump cannot trigger it — a storm needs a few frames of real motion.
 *
 * The arithmetic behind the number, since it would otherwise read as arbitrary.
 * Accumulated energy settles at `perFrameJerk / (1 - SHAKE_DECAY)`, so with a
 * decay of 0.82 the steady state is about 5.6x the per-frame jerk. Walking is
 * roughly +/-2 m/s^2 at ~2 Hz, which at a 60 Hz sample rate is ~0.4 per frame
 * and settles near 2.2. A deliberate shake is +/-20 m/s^2 at ~5 Hz: ~10 per
 * frame, settling near 58. 14 sits in the wide gap between them, nearer the
 * quiet end so the gesture stays easy to perform.
 */
export const SHAKE_TRIGGER = 14;

/** How fast accumulated jerk bleeds away, per frame, when the phone is still. */
export const SHAKE_DECAY = 0.82;

/**
 * Minimum gap between storms.
 *
 * Not "one shake, one storm": a sustained shake SHOULD keep the jar churning,
 * which is what the gesture means. This only stops the storm restarting on
 * every sensor frame, which would pin it at full strength and never let it
 * settle.
 */
export const SHAKE_COOLDOWN_MS = 550;

/**
 * Below this, a press is a tap (stir). At or above it, a hold (gather).
 *
 * The gather also BEGINS at this delay, so the crowd starts moving exactly when
 * the press qualifies as a hold — the same constant on both sides, or a tap
 * would visibly disturb the jar before deciding it was only a tap.
 */
export const HOLD_THRESHOLD_MS = 240;

/**
 * Every duration the jar animates over, in one table.
 *
 * Gathered here because they were ten magic numbers scattered across the
 * screen, and "make it faster" then meant finding all ten. Feel is tuned by
 * editing this block and nothing else.
 *
 * Most values are roughly 1.7x quicker than the first pass, which read as
 * syrupy on a device: the gestures are meant to feel like liquid answering a
 * hand, and a jar that takes most of a second to acknowledge a tilt feels
 * broken rather than heavy.
 *
 * `stir` is the deliberate exception. It went to 540 with the rest and was put
 * back near its original pace: the tap is the jar's idle, most-repeated
 * gesture, and hurrying it made the marbles look flicked rather than stirred.
 * The reactive gestures want to feel immediate; this one wants to feel like it
 * has weight.
 */
export const JAR_TIMING = {
  /** Tap-stir, and its Reduce Motion counterpart. Slower than the rest on purpose. */
  stir: 820,
  stirReduced: 200,
  /** Hold: drawing the crowd inward. */
  gather: 460,
  /** Unwinding a gather that turned out to be a tap. */
  gatherCancel: 130,
  /** Unwinding a real hold, as the scatter fires. */
  gatherRelease: 280,
  /** Release burst: out fast, settle slower. */
  scatterOut: 80,
  scatterSettle: 520,
  /** Shake: rise, then decay back to rest. */
  stormRise: 100,
  stormSettle: 1400,
  /** One orbit of the storm churn. */
  stormOrbit: 620,
  /** Levelling the jar when the screen loses focus. */
  tiltLevel: 160,
} as const;

export type Vec2 = { x: number; y: number };

/**
 * DECLARED FIRST, and that is load-bearing rather than tidy.
 *
 * `clamp` is itself a worklet, and the worklets Babel plugin builds each
 * worklet's closure AT ITS DEFINITION SITE during module evaluation. A worklet
 * that calls one defined later therefore reads a binding that has not been
 * initialised yet, and the screen dies on load with
 * "Cannot access 'clamp' before initialization" — which is exactly what
 * happened the first time this file shipped, with `clamp` at the bottom.
 *
 * Ordinary function declarations hoist and would not care. Worklets do not get
 * that, so every worklet helper in this file must appear ABOVE its callers.
 */
export function clamp(n: number, min: number, max: number): number {
  "worklet";
  return Math.min(Math.max(n, min), max);
}

/**
 * Raw gravity to a tilt vector in [-1, 1], where +x is "the right edge is
 * lower" and +y is "the bottom edge is lower".
 *
 * `interfaceOrientation` is NOT consulted here: the sensor hook is configured
 * with `adjustToInterfaceOrientation`, so the values arrive already rotated
 * into the interface's frame. Doing it twice would cancel out in portrait and
 * be wrong everywhere else.
 *
 * The screen's y axis grows downward while gravity's grows up, hence the
 * negation — without it the bubbles drift toward the RAISED side, which reads
 * as the jar being upside down.
 */
export function gravityToTilt(g: { x: number; y: number }): Vec2 {
  "worklet";
  return {
    x: clamp(g.x / G, -1, 1),
    y: clamp(-g.y / G, -1, 1),
  };
}

/**
 * One step of a low-pass filter.
 *
 * Raw accelerometer output is far too noisy to drive a position directly — a
 * still phone jitters by a few hundredths of a g, and 35 bubbles all twitching
 * in unison looks like a rendering fault rather than a liquid.
 */
export function smoothToward(current: number, target: number, rate: number): number {
  "worklet";
  return current + (target - current) * rate;
}

export type Vec3 = { x: number; y: number; z: number };

/**
 * Fold one accelerometer sample into the shake accumulator.
 *
 * Returns the new accumulated energy and the sample, which the caller feeds
 * back next frame. Comparing against {@link SHAKE_TRIGGER} and applying the
 * cooldown are the CALLER's job — keeping that out of here is what lets this be
 * tested without a clock.
 *
 * ## Why the whole VECTOR is differenced, not its magnitude
 *
 * The first version differenced magnitudes and could not detect a shake at all.
 * Shaking a phone reverses its acceleration while its speed stays roughly
 * constant, so `|a|` barely changes at the extremes — the very moments that
 * make it a shake. The test caught it: a clean +/-22 alternation registered
 * 1.6 against a threshold of 3.2.
 *
 * Differencing the vector fixes that and, as a bonus, keeps the property the
 * magnitude version was written for: a constant gravity offset cancels, since
 * `|(a + g) - (a' + g)| === |a - a'|`. So the threshold means the same thing
 * whether or not the platform folds gravity into the reading — which matters,
 * because iOS and Android disagree about that.
 */
export function accumulateShake(
  previousEnergy: number,
  previous: Vec3,
  sample: Vec3,
): { energy: number; previous: Vec3 } {
  "worklet";
  const dx = sample.x - previous.x;
  const dy = sample.y - previous.y;
  const dz = sample.z - previous.z;
  const jerk = Math.sqrt(dx * dx + dy * dy + dz * dz);
  // Decay first, then add: a phone at rest trends to zero rather than holding
  // the last spike forever.
  const energy = previousEnergy * SHAKE_DECAY + jerk;
  return { energy, previous: sample };
}

/**
 * The per-bubble constants the worklets need, computed once in JS.
 *
 * Derived from the bubble's own seed so the motion is stable across renders —
 * a bubble that re-randomised every frame would shimmer. Kept as plain numbers
 * because a worklet may capture those freely, while it may not capture the
 * seeded-RNG closure that produced them.
 */
export type TokenMotionConstants = {
  /** How strongly this bubble answers a tilt. Heavier bubbles lag. */
  driftBias: number;
  /**
   * SYMMETRIC room, used only as the MAGNITUDE of a tilt or a storm orbit, so
   * those stay gentle and even-handed wherever the bubble happens to sit.
   * Never used as a limit — see the clamp fields below.
   */
  driftSpanX: number;
  driftSpanY: number;
  /** Direction and distance to the jar's middle, for `hold`. */
  gatherX: number;
  gatherY: number;
  /** Outward direction for the release scatter. */
  scatterX: number;
  scatterY: number;
  /** Orbit parameters for the shake storm. */
  stormPhase: number;
  stormRadius: number;
  /**
   * The TRUE room this bubble has, per direction, in translate space.
   *
   * Asymmetric on purpose, and this is the correction that made `hold`
   * actually gather. The first version clamped to a symmetric
   * `min(roomLeft, roomRight)`, reasoning that it could then never be pushed
   * through the glass. It could not — but a bubble sitting near a wall has
   * almost no room on that side, so its symmetric limit was almost nothing in
   * BOTH directions, and it could not travel to the middle either. The crowd
   * gathered into a loose ring with a hole in it rather than a clump.
   *
   * Moving toward the far wall can never leave the glass, so the honest bound
   * is the real distance in each direction.
   */
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

/**
 * Everything the four gestures contribute, summed and then clamped ONCE.
 *
 * Clamping per-effect instead would let a bubble sit at its limit under tilt
 * and still be shoved further by a shake. Summing first and clamping last is
 * what keeps the glass solid no matter how many gestures overlap.
 *
 * `intensity` is the accessibility damper: 1 is the full effect, and Reduce
 * Motion drives it toward 0 without changing any of the arithmetic below, so
 * the reduced version cannot drift out of agreement with the full one.
 */
export function composeTokenOffset(
  k: TokenMotionConstants,
  state: {
    tiltX: number;
    tiltY: number;
    gather: number;
    scatter: number;
    storm: number;
    stormClock: number;
  },
  intensity: number,
): Vec2 {
  "worklet";
  const drift = k.driftBias * intensity;

  // Tilt: a steady lean toward the low side.
  let x = state.tiltX * drift * k.driftSpanX;
  let y = state.tiltY * drift * k.driftSpanY;

  // Hold: pull toward the middle. Scaled by `gather`, so releasing unwinds it.
  x += k.gatherX * state.gather * intensity;
  y += k.gatherY * state.gather * intensity;

  // Release: a brief outward burst.
  x += k.scatterX * state.scatter * intensity;
  y += k.scatterY * state.scatter * intensity;

  // Shake: a decaying orbit, each bubble on its own phase so the crowd churns
  // instead of sliding as one rigid block.
  if (state.storm > 0) {
    const angle = state.stormClock * 2 * Math.PI + k.stormPhase;
    const radius = k.stormRadius * state.storm * intensity;
    x += Math.cos(angle) * radius;
    y += Math.sin(angle * 1.37) * radius;
  }

  // Clamped once, against the REAL room in each direction. Clamping per-effect
  // would let a bubble sit at its tilt limit and still be shoved further by a
  // shake; clamping symmetrically would stop it reaching the middle on a hold.
  return { x: clamp(x, k.minX, k.maxX), y: clamp(y, k.minY, k.maxY) };
}
